'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react'
import NavItem, { NavIds, navItems } from '@/app/components/ui/navigation/nav'
import { AppRoutes } from '@/lib/constants/routes'

interface NavigationContextType {
	selectedNavItem: NavItem | undefined
	setSelectedNavItem: (item: NavItem | undefined) => void
	selectedAppBarNavItem: NavItem | undefined
	setSelectedAppBarNavItem: (item: NavItem | undefined) => void
	showTopNav?: boolean
	setShowTopNav?: (showTopNav: boolean) => void
	showLeftNav?: boolean
	setShowLeftNav?: (showLeftNav: boolean) => void
	isLeftNavExpanded?: boolean
	setIsLeftNavExpanded?: (isLeftNavExpanded: boolean) => void
	toggleLeftNavExpanded?: (value: boolean) => void
	toggleInitialLogin?: (value: boolean) => void
	initialLogin: boolean | null
	setInitialLogin: (initialLogin: boolean) => void
	selectedNavItemBeforeSignout: NavItem | undefined
	setSelectedNavItemBeforeSignout: (item: NavItem | undefined) => void
	refreshNavigationState: () => void
	isNavigationConsistent: () => boolean
}

export const NavigationContext = createContext<NavigationContextType | undefined>(undefined)

/**
 * Extracts the module base path from a full route path.
 * Used for matching navigation items when users access sub-routes within modules.
 */
const getModuleBasePath = (path: string): string | null => {
	const lowerPath = path.toLowerCase()

	// Extract module paths for navigation matching using route constants
	if (lowerPath.startsWith(AppRoutes.DASHBOARD.toLowerCase())) {
		return AppRoutes.DASHBOARD
	}
	if (lowerPath.startsWith(AppRoutes.RECRUITING.toLowerCase())) {
		return AppRoutes.RECRUITING
	}
	if (lowerPath.startsWith(AppRoutes.AGENT_MANAGEMENT.toLowerCase())) {
		return AppRoutes.AGENT_MANAGEMENT
	}
	// '/auth/signout' is not in AppRoutes, keep as literal
	if (lowerPath.startsWith('/auth/signout')) {
		return '/auth/signout'
	}

	return null
}

/**
 * Enhanced navigation selector that handles permission-based routing and path matching.
 * Supports exact path matching and module-based path matching for permission-aware navigation.
 */
const navSelector = (currentPath: string): NavItem | undefined => {
	// Handle root path - should not match any navigation item
	if (currentPath === '/') {
		return undefined
	}

	const decodedPath = decodeURIComponent(currentPath)

	// Handle specific recruiting pipeline route
	if (decodedPath.toLowerCase() === '/feature/recruiting/pipeline') {
		return navItems.find(item => item.navId === NavIds.Recruiting) // Index of "Recruiting"
	}

	// First try exact URL matching for precise navigation
	let navItem = navItems.find(item => item.url.toLowerCase() === decodedPath.toLowerCase())

	// If exact match found, return it
	if (navItem) {
		return navItem
	}

	// For permission-based routing, try module-based matching
	// This handles cases where users are redirected to module root paths
	const moduleBasePath = getModuleBasePath(decodedPath)
	if (moduleBasePath) {
		navItem = navItems.find(item => getModuleBasePath(item.url) === moduleBasePath)
	}

	return navItem
}

export const NavigationProvider = ({ currentPath, children }: { currentPath: string; children: ReactNode }) => {
	const [selectedNavItem, setSelectedNavItem] = useState<NavItem | undefined>(() => navSelector(currentPath))
	const [selectedAppBarNavItem, setSelectedAppBarNavItem] = useState<NavItem | undefined>(() => navSelector(currentPath))
	const [showTopNav, setShowTopNav] = useState(true)
	const [showLeftNav, setShowLeftNav] = useState(false)
	const [isLeftNavExpanded, setIsLeftNavExpanded] = useState(() => {
		if (typeof window !== 'undefined') {
			const stored = window.localStorage.getItem('boss-ui-leftNavExpanded')
			if (stored !== null) {
				return stored === 'true'
			}
		}
		return true
	})
	const [initialLogin, setInitialLogin] = useState<boolean | null>(null)
	const [selectedNavItemBeforeSignout, setSelectedNavItemBeforeSignout] = useState<NavItem | undefined>(undefined)

	const toggleLeftNavExpanded = (value: boolean) => {
		setIsLeftNavExpanded(!value)
	}
	const toggleInitialLogin = (value: boolean) => {
		setInitialLogin(!value)
	}

	/**
	 * Forces navigation index update based on current path.
	 * Useful after permission-based redirects to ensure navigation state consistency.
	 */
	const refreshNavigationState = useCallback(() => {
		const newItem = navSelector(currentPath)
		setSelectedNavItem(newItem)
		setSelectedAppBarNavItem(newItem)
	}, [currentPath])

	/**
	 * Checks if the current path matches the selected navigation item.
	 * Used to detect navigation inconsistencies after redirects.
	 */
	const isNavigationConsistent = useCallback((): boolean => {
		const expectedIndex = navSelector(currentPath)
		return expectedIndex === selectedNavItem && expectedIndex === selectedAppBarNavItem
	}, [currentPath, selectedNavItem, selectedAppBarNavItem])

	useEffect(() => {
		const newItem = navSelector(currentPath)
		setSelectedNavItem(newItem)
		setSelectedAppBarNavItem(newItem)
	}, [currentPath])

	// Persist isLeftNavExpanded to localStorage whenever it changes
	useEffect(() => {
		if (typeof window !== 'undefined') {
			window.localStorage.setItem('boss-ui-leftNavExpanded', String(isLeftNavExpanded))
		}
	}, [isLeftNavExpanded])

	const contextValue = useMemo(
		() => ({
			selectedNavItem,
			setSelectedNavItem,
			selectedAppBarNavItem,
			setSelectedAppBarNavItem,
			showLeftNav,
			setShowLeftNav,
			showTopNav,
			setShowTopNav,
			isLeftNavExpanded,
			setIsLeftNavExpanded,
			toggleLeftNavExpanded,
			toggleInitialLogin,
			initialLogin,
			setInitialLogin,
			selectedNavItemBeforeSignout,
			setSelectedNavItemBeforeSignout,
			refreshNavigationState,
			isNavigationConsistent,
		}),
		[
			selectedNavItem,
			selectedAppBarNavItem,
			showLeftNav,
			showTopNav,
			isLeftNavExpanded,
			initialLogin,
			selectedNavItemBeforeSignout,
			refreshNavigationState,
			isNavigationConsistent,
		]
	)

	return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>
}

export const useNavigation = () => {
	const context = useContext(NavigationContext)
	if (!context) {
		throw new Error('useNavigation must be used within a NavigationProvider')
	}
	return context
}
